import {
  STAGE_H,
  STAGE_W,
  type DesignSubmission,
  type ShapeInstance,
} from "@poppycock/shared";

function centerOf(s: ShapeInstance) {
  return { cx: s.x + s.w / 2, cy: s.y + s.h / 2 };
}

function sortedByZ(shapes: ShapeInstance[]): ShapeInstance[] {
  return [...shapes].sort((a, b) => a.z - b.z);
}

/** Pure SVG shape from serializable data. */
export function StaticShape({ shape }: { shape: ShapeInstance }) {
  const { cx, cy } = centerOf(shape);
  const transform = `rotate(${shape.rotation} ${cx} ${cy})`;
  const fill = shape.color;
  const stroke =
    shape.color === "#FFFFFF" ? "rgba(0,0,0,0.12)" : "transparent";
  const strokeW = shape.color === "#FFFFFF" ? 1 : 0;

  switch (shape.type) {
    case "circle":
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={shape.w / 2}
          ry={shape.h / 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeW}
          transform={transform}
        />
      );
    case "rect":
      return (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeW}
          transform={transform}
        />
      );
    case "roundRect":
      return (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.w}
          height={shape.h}
          rx={Math.min(shape.w, shape.h) * 0.22}
          ry={Math.min(shape.w, shape.h) * 0.22}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeW}
          transform={transform}
        />
      );
    case "triangle": {
      const points = [
        `${cx},${shape.y}`,
        `${shape.x + shape.w},${shape.y + shape.h}`,
        `${shape.x},${shape.y + shape.h}`,
      ].join(" ");
      return (
        <polygon
          points={points}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeW}
          transform={transform}
        />
      );
    }
    case "line": {
      const yMid = shape.y + shape.h / 2;
      return (
        <line
          x1={shape.x}
          y1={yMid}
          x2={shape.x + shape.w}
          y2={yMid}
          stroke={fill}
          strokeWidth={Math.max(shape.h, 4)}
          strokeLinecap="round"
          transform={transform}
        />
      );
    }
  }
}

export function DesignStageSvg({
  shapes,
  className = "",
  label = "Design",
}: {
  shapes: ShapeInstance[];
  className?: string;
  label?: string;
}) {
  return (
    <svg
      viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
      className={className}
      role="img"
      aria-label={label}
    >
      <rect x={0} y={0} width={STAGE_W} height={STAGE_H} fill="#F4F2EE" />
      {sortedByZ(shapes).map((s) => (
        <StaticShape key={s.id} shape={s} />
      ))}
    </svg>
  );
}

/** Compact card body for text or design answers. */
export function AnswerBody({
  answer,
  className = "",
}: {
  answer: {
    kind?: string;
    text?: string;
    design?: DesignSubmission;
  };
  className?: string;
}) {
  if (answer.kind === "design" && answer.design?.shapes?.length) {
    return (
      <div
        className={`overflow-hidden rounded-xl border border-black/10 ${className}`}
      >
        <DesignStageSvg
          shapes={answer.design.shapes}
          className="block h-auto w-full"
          label="Player design"
        />
      </div>
    );
  }
  return (
    <p className={`text-[15px] leading-relaxed ${className}`}>
      {answer.text || "…"}
    </p>
  );
}
